import React, { useState, useRef, useEffect } from 'react';
import { uploadImage, uploadVideo } from '../../api/uploads.services';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import toast from '../../lib/toast';
import './imageUpload.scss';

const VIDEO_EXTENSIONS = /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$)/i;

// Видео определяем и по accept (форма знает, что грузит), и по самому URL:
// в базе уже лежат ролики, сохранённые через поля без accept="video/*".
const isVideoSource = (accept, url) =>
  Boolean((accept && accept.includes('video')) || (url && VIDEO_EXTENSIONS.test(url)));

// `currentImage` — легаси-имя пропа (так его передаёт форма отзывов),
// поддерживаем оба, чтобы аватар не оставался с пустым превью.
const ImageUpload = ({
  value,
  currentImage,
  onChange,
  label,
  error,
  touched,
  accept = 'image/*',
  uploadPath,
}) => {
  const currentValue = value ?? currentImage ?? '';
  const [preview, setPreview] = useState(currentValue);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Значение приходит асинхронно (форма редактирования грузит данные с API)
  // и меняется извне — без синхронизации превью залипало на initial-значении.
  useEffect(() => {
    setPreview((prev) => {
      if (prev === currentValue) return prev;
      if (typeof prev === 'string' && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return currentValue;
    });
  }, [currentValue]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 20MB for images)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Файл занадто великий. Максимум 20 МБ.');
      return;
    }

    // Validate file type
    const isVideo = accept && accept.includes('video');
    const isImage = !isVideo;

    if (isVideo && !file.type.startsWith('video/')) {
      toast.error('Будь ласка, виберіть відеофайл.');
      return;
    }

    if (isImage && !file.type.startsWith('image/')) {
      toast.error('Будь ласка, виберіть зображення.');
      return;
    }

    setIsLoading(true);
    try {
      // Show local preview while uploading
      const localPreview = URL.createObjectURL(file);
      setPreview(localPreview);

      // Upload file
      const isVideo = accept && accept.includes('video');
      const response = isVideo
        ? await uploadVideo(file, uploadPath)
        : await uploadImage(file, uploadPath);

      if (response?.url) {
        onChange(response.url);
        setPreview(response.url);
        const isVideo = accept && accept.includes('video');
        const successMsg = isVideo ? 'Відео успішно загружено!' : 'Зображення успішно загружено!';
        toast.success(successMsg);
      } else {
        throw new Error('Відповідь сервера не містить URL');
      }
    } catch (err) {
      setPreview(value);
      const isVideo = accept && accept.includes('video');
      const errorMsg = isVideo ? 'Помилка при загруженні відео' : 'Помилка при загруженні зображення';
      toast.error(err.message || errorMsg);
      console.error('Upload error:', err);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="form-group">
      {label && <label>{label}</label>}

      <div className={`image-upload ${touched && error ? 'error' : ''}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={isLoading}
          style={{ display: 'none' }}
        />

        {preview ? (
          <div
            className={`image-upload__preview ${
              isVideoSource(accept, preview) ? 'image-upload__preview--video' : ''
            }`}
          >
            {isVideoSource(accept, preview) ? (
              <video
                src={resolveMediaUrl(preview)}
                controls
                preload="metadata"
                playsInline
              />
            ) : (
              <img src={resolveMediaUrl(preview)} alt="Preview" />
            )}
            <button
              type="button"
              className="image-upload__change"
              onClick={handleClick}
              disabled={isLoading}
            >
              {isLoading ? 'Завантажується...' : 'Замінити'}
            </button>
          </div>
        ) : (
          <div
            className="image-upload__placeholder"
            onClick={handleClick}
          >
            <div className="image-upload__icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path
                  d="M20 2C10.06 2 2 10.06 2 20s8.06 18 18 18 18-8.06 18-18S29.94 2 20 2zm0 32c-7.73 0-14-6.27-14-14s6.27-14 14-14 14 6.27 14 14-6.27 14-14 14z"
                  fill="#081249"
                />
                <path
                  d="M20 10c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm0 16c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"
                  fill="#081249"
                />
              </svg>
            </div>
            <p className="image-upload__text">
              {isVideoSource(accept) ? 'Клікніть для вибору відео' : 'Клікніть для вибору зображення'}
            </p>
            <p className="image-upload__hint">Або перетягніть файл сюди</p>
          </div>
        )}
      </div>

      {touched && error && <span className="form-error">{error}</span>}
    </div>
  );
};

export default ImageUpload;
